import { reportProviderWorkingLocation } from "../provider-workspace/provider-working-location";
import FullCalendar from "@fullcalendar/react";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import timeGridPlugin from "@fullcalendar/timegrid";
import type {
  DatesSetArg,
  DateSelectArg,
  EventApi,
  EventClickArg,
  EventContentArg,
  EventDropArg,
} from "@fullcalendar/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { providerApi } from "../../lib/api";
import type {
  BookingStatus,
  ProviderBooking,
  ProviderSession,
  ProviderVenue,
} from "../../lib/api";
import { useProviderWorkspace } from "../provider-workspace/ProviderWorkspaceContext";
import { BookingDetailCard } from "./BookingDetailCard";
import {
  BookingFormCard,
  createEmptyBookingDraft,
  type ProviderBookingDraft,
} from "./BookingFormCard";
import {
  buildCalendarEvents,
  deriveSlotMaxMinutes,
  deriveSlotMinMinutes,
  formatVisibleRangeLabel,
  formatCalendarRangeLabel,
  formatDateTimeForPicker,
  formatSlotTime,
} from "./booking-utils";
import { useProviderBookings } from "./useProviderBookings";
import { useAppPreferences } from "../../lib/preferences";
import { BookingListView } from "./BookingListView";
import { UpcomingBookings } from "./UpcomingBookings";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../components/ui/Dialog";
import { useMediaQuery } from "../../lib/use-media-query";

/** Upcoming (default) and Past are lists; Day and Week are the calendar grid. */
type CalendarViewMode = "upcoming" | "timeGridDay" | "timeGridWeek" | "past";
const UPCOMING_DAYS = 30;
const PAST_DAYS = 30;
const CALENDAR_SLOT_MINUTES = 6 * 60;
const COMPACT_CALENDAR_QUERY = "(max-width: 640px)";

interface BookingRescheduleInfo {
  event: EventApi;
  revert: () => void;
}

function getEventBooking(event: EventApi) {
  const candidate = event.extendedProps.booking;

  if (!candidate || typeof candidate !== "object" || !("id" in candidate)) {
    return null;
  }

  return candidate as ProviderBooking;
}

function createDraftFromBooking(booking: ProviderBooking): ProviderBookingDraft {
  return {
    unit_id: booking.unit_id,
    customer_name: booking.customer_name,
    customer_email: booking.customer_email ?? "",
    customer_phone: booking.customer_phone ?? "",
    scheduled_start_local: formatDateTimeForPicker(
      booking.scheduled_start_at,
      booking.venue_timezone,
    ),
    scheduled_end_local: formatDateTimeForPicker(
      booking.scheduled_end_at,
      booking.venue_timezone,
    ),
    notes: booking.notes ?? "",
  };
}

function applyActiveSessionToBooking(
  booking: ProviderBooking,
  activeSession: ProviderSession | null,
) {
  if (!activeSession) {
    return booking;
  }

  return {
    ...booking,
    status: "checked_in",
    scheduled_end_at: activeSession.planned_end_at ?? booking.scheduled_end_at,
    updated_at: activeSession.updated_at,
  } satisfies ProviderBooking;
}

function getSelectionDraft(
  selection: DateSelectArg,
  selectedVenue: ProviderVenue | null,
  preferredUnitId: string | null,
): ProviderBookingDraft {
  const draft = createEmptyBookingDraft(selectedVenue, preferredUnitId);

  if (!selectedVenue) {
    return draft;
  }

  return {
    ...draft,
    scheduled_start_local: formatDateTimeForPicker(
      selection.start,
      selectedVenue.timezone,
    ),
    scheduled_end_local: formatDateTimeForPicker(
      selection.end,
      selectedVenue.timezone,
    ),
  };
}

/** Day-of-week (0=Sunday) of "today" in the venue timezone, used as firstDay. */
function getVenueTodayDayOfWeek(timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: timezone,
  }).formatToParts(new Date());
  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  const day = Number(parts.find((part) => part.type === "day")?.value);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day)
  ) {
    return new Date().getDay();
  }

  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function getVenueLocalMinutes(timezone: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: timezone,
  }).formatToParts(new Date());
  const hour = Number(parts.find((part) => part.type === "hour")?.value);
  const minute = Number(parts.find((part) => part.type === "minute")?.value);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return CALENDAR_SLOT_MINUTES;
  }

  return hour * 60 + minute;
}

export function BookingsCard({
  activeSessionsByBookingId,
  createRequestToken,
  onStartSessionFromBooking,
  pendingReviewToken,
  preferredUnitId,
  readOnly = false,
  refreshToken,
  selectedVenue,
  startingSessionBookingId,
}: {
  activeSessionsByBookingId: Record<string, ProviderSession>;
  createRequestToken: number;
  onStartSessionFromBooking: (
    booking: ProviderBooking,
    note: string,
  ) => Promise<ProviderSession | null>;
  pendingReviewToken?: number;
  preferredUnitId: string | null;
  readOnly?: boolean;
  refreshToken: number;
  selectedVenue: ProviderVenue | null;
  startingSessionBookingId: string | null;
}) {
  const { t, resolvedTheme, resolvedLanguage } = useAppPreferences();
  const { notificationTarget } = useProviderWorkspace();
  const calendarRef = useRef<FullCalendar | null>(null);
  const isCompactCalendar = useMediaQuery(COMPACT_CALENDAR_QUERY);
  const [viewMode, setViewMode] = useState<CalendarViewMode>("upcoming");
  const isCalendarView = viewMode === "timeGridDay" || viewMode === "timeGridWeek";
  // The range FullCalendar is actually showing, for an accurate header label.
  const [visibleRange, setVisibleRange] = useState<{ start: Date; end: Date } | null>(null);
  // A booking opened from a notification or just saved may sit outside the loaded
  // dates; keep its record so its details still open.
  const [pinnedBooking, setPinnedBooking] = useState<ProviderBooking | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [visibleFromLocal, setVisibleFromLocal] = useState<string | null>(null);
  const [visibleToLocal, setVisibleToLocal] = useState<string | null>(null);
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProviderBookingDraft | null>(null);
  const lastCreateRequestToken = useRef(0);
  const lastPendingReviewToken = useRef(0);
  const hasInitialCalendarScroll = useRef(false);
  const handledNotificationTargetRef = useRef<typeof notificationTarget>(null);
  const fetchingNotificationTargetRef = useRef<typeof notificationTarget>(null);
  const currentNotificationTargetRef = useRef(notificationTarget);
  currentNotificationTargetRef.current = notificationTarget;
  const currentVenueIdRef = useRef(selectedVenue?.id);
  currentVenueIdRef.current = selectedVenue?.id;
  const pendingRevealBookingIdRef = useRef<string | null>(null);

  const {
    bookings,
    isLoading,
    error,
    notice,
    creating,
    savingBookingId,
    updatingStatusId,
    createBooking,
    updateBooking,
    updateBookingStatus,
    reload,
  } = useProviderBookings({
    scheduledFromLocal: visibleFromLocal,
    scheduledToLocal: visibleToLocal,
    selectedVenue,
  });

  // Schedule-aware grid bottom: reach the venue's latest (possibly next-day) close
  // instead of a hardcoded 02:00 cap, so overnight hours are never clipped.
  const slotMaxMinutes = useMemo(
    () => deriveSlotMaxMinutes(selectedVenue?.operating_hours),
    [selectedVenue?.operating_hours],
  );
  const slotMinMinutes = useMemo(
    () => Math.min(deriveSlotMinMinutes(selectedVenue?.operating_hours), slotMaxMinutes - CALENDAR_SLOT_MINUTES / 6),
    [selectedVenue?.operating_hours, slotMaxMinutes],
  );

  // Compact viewports need short weekday/date headers and a slim time axis, or the
  // seven week columns clip their cushions. Desktop keeps FullCalendar defaults.
  const dayHeaderFormat = useMemo(() => {
    if (!isCompactCalendar) {
      return undefined;
    }
    return viewMode === "timeGridDay"
      ? ({ weekday: "short", month: "short", day: "numeric" } as const)
      : ({ weekday: "narrow", day: "numeric" } as const);
  }, [isCompactCalendar, viewMode]);
  const slotLabelFormat = useMemo(
    () =>
      isCompactCalendar
        ? ({
            hour: "numeric",
            minute: "2-digit",
            meridiem: "short",
            omitZeroMinute: true,
          } as const)
        : undefined,
    [isCompactCalendar],
  );

  useEffect(() => {
    if (!selectedVenue) {
      setSelectedBookingId(null);
      setEditingBookingId(null);
      setDraft(null);
    }
  }, [selectedVenue]);

  // Do not carry a previous venue's calendar window into a venue with a
  // different local day or timezone. FullCalendar will then apply the new
  // firstDay and timezone to the venue's local today.
  useEffect(() => {
    hasInitialCalendarScroll.current = false;
    if (!selectedVenue) {
      return;
    }

    const today = new Date();
    setCalendarDate(today);
    calendarRef.current?.getApi().gotoDate(today);
  }, [selectedVenue?.id, selectedVenue?.timezone]);

  useEffect(() => {
    if (
      hasInitialCalendarScroll.current ||
      !selectedVenue ||
      !isCalendarView ||
      !calendarRef.current
    ) {
      return;
    }

    hasInitialCalendarScroll.current = true;
    const localMinutes = getVenueLocalMinutes(selectedVenue.timezone);
    const clampedMinutes = Math.min(
      slotMaxMinutes,
      Math.max(slotMinMinutes, localMinutes),
    );
    const scrollTimer = window.setTimeout(() => {
      calendarRef.current?.getApi().scrollToTime({
        hours: Math.floor(clampedMinutes / 60),
        minutes: clampedMinutes % 60,
      });
    }, 0);

    return () => window.clearTimeout(scrollTimer);
  }, [selectedVenue?.id, selectedVenue?.timezone, viewMode, slotMaxMinutes, slotMinMinutes]);

  useEffect(() => {
    // A notification or saved-booking selection stays pending until the visible-window
    // reload brings the target booking into `bookings`; skip auto-clearing it
    // in the meantime, then release the guard so normal behavior resumes.
    if (
      pendingRevealBookingIdRef.current &&
      bookings.some((booking) => booking.id === pendingRevealBookingIdRef.current)
    ) {
      pendingRevealBookingIdRef.current = null;
    }

    if (
      selectedBookingId &&
      !bookings.some((booking) => booking.id === selectedBookingId) &&
      pendingRevealBookingIdRef.current !== selectedBookingId &&
      pinnedBooking?.id !== selectedBookingId
    ) {
      setSelectedBookingId(null);
    }
  }, [bookings, pinnedBooking?.id, selectedBookingId]);

  // Consume each booking notification visit once. ProviderLayout
  // owns venue switching, so we only act after the selected venue matches the
  // target venue; then we fetch the booking if it is not already loaded, reset
  // the draft, move the calendar window to its scheduled start, and select
  // it to open the detail card.
  useEffect(() => {
    if (!notificationTarget || notificationTarget.kind !== "booking") {
      return;
    }

    const targetKey = notificationTarget;
    if (
      handledNotificationTargetRef.current === targetKey ||
      fetchingNotificationTargetRef.current === targetKey
    ) {
      return;
    }

    if (
      notificationTarget.venueId &&
      selectedVenue?.id !== notificationTarget.venueId
    ) {
      return;
    }

    if (!selectedVenue) {
      return;
    }

    const revealBooking = (booking: ProviderBooking) => {
      if (currentNotificationTargetRef.current !== notificationTarget || currentVenueIdRef.current !== selectedVenue.id || booking.venue_id !== selectedVenue.id) return;
      pendingRevealBookingIdRef.current = booking.id;
      setEditingBookingId(null);
      setDraft(null);
      const scheduledStart = new Date(booking.scheduled_start_at);
      setCalendarDate(scheduledStart);
      calendarRef.current?.getApi().gotoDate(scheduledStart);
      setPinnedBooking(booking);
      setSelectedBookingId(booking.id);
      handledNotificationTargetRef.current = targetKey;
    };

    const loadedBooking = bookings.find(
      (booking) => booking.id === notificationTarget.bookingId,
    );
    if (loadedBooking) {
      revealBooking(loadedBooking);
      return;
    }

    fetchingNotificationTargetRef.current = targetKey;
    const targetRequest = readOnly
      ? providerApi.listBookings({ venueId: selectedVenue.id }).then((response) => {
          const booking = response.bookings.find(({ id }) => id === notificationTarget.bookingId);
          if (!booking) throw new Error("Booking is not available in this workspace.");
          return booking;
        })
      : providerApi.getBooking(notificationTarget.bookingId);
    targetRequest
      .then((booking) => {
        if (fetchingNotificationTargetRef.current !== targetKey) {
          return;
        }
        fetchingNotificationTargetRef.current = null;
        revealBooking(booking);
      })
      .catch(() => {
        // Leave the calendar untouched when the target cannot be resolved
        // (deleted booking, offline, ...); a later target change can retry.
        if (fetchingNotificationTargetRef.current === targetKey) {
          fetchingNotificationTargetRef.current = null;
        }
      });
  }, [bookings, notificationTarget, readOnly, selectedVenue]);

  useEffect(() => {
    void reload();
  }, [refreshToken, reload]);

  useEffect(() => {
    if (readOnly || createRequestToken === 0 || createRequestToken === lastCreateRequestToken.current) {
      return;
    }

    lastCreateRequestToken.current = createRequestToken;
    calendarRef.current?.getApi().unselect();
    handleOpenCreate();
  }, [createRequestToken, preferredUnitId, readOnly, selectedVenue]);

  // Review returns to today while keeping every booking status visible.
  useEffect(() => {
    if (!pendingReviewToken || pendingReviewToken === lastPendingReviewToken.current) {
      return;
    }

    lastPendingReviewToken.current = pendingReviewToken;
    setSelectedBookingId(null);
    setEditingBookingId(null);
    setDraft(null);
    setCalendarDate(new Date());
    setViewMode("upcoming");
  }, [pendingReviewToken]);

  useEffect(() => {
    if (isCalendarView || !selectedVenue) {
      return;
    }

    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    if (viewMode === "upcoming") {
      start.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() + UPCOMING_DAYS);
    } else {
      start.setDate(start.getDate() - PAST_DAYS);
    }

    setVisibleFromLocal(formatDateTimeForPicker(start, selectedVenue.timezone));
    setVisibleToLocal(formatDateTimeForPicker(end, selectedVenue.timezone));
  }, [isCalendarView, viewMode, selectedVenue]);

  const resolvedBookings = useMemo(
    () =>
      bookings.map((booking) =>
        applyActiveSessionToBooking(
          booking,
          activeSessionsByBookingId[booking.id] ?? null,
        ),
      ),
    [activeSessionsByBookingId, bookings],
  );
  const selectedBooking =
    resolvedBookings.find((booking) => booking.id === selectedBookingId) ??
    (pinnedBooking && pinnedBooking.id === selectedBookingId
      ? applyActiveSessionToBooking(pinnedBooking, activeSessionsByBookingId[pinnedBooking.id] ?? null)
      : null);
  useEffect(() => {
    if (!selectedVenue || (notificationTarget?.kind === "booking" && !selectedBooking)) return;
    reportProviderWorkingLocation({ destination: "schedule", context: selectedBooking
      ? { kind: "booking", venue_id: selectedVenue.id, record_id: selectedBooking.id }
      : { kind: "venue", venue_id: selectedVenue.id } });
  }, [selectedVenue?.id, selectedBooking?.id, notificationTarget]);

  const selectedBookingSession = selectedBooking
    ? activeSessionsByBookingId[selectedBooking.id] ?? null
    : null;
  const calendarEvents = useMemo(
    () => buildCalendarEvents(resolvedBookings, selectedBookingId, resolvedTheme),
    [resolvedBookings, selectedBookingId, resolvedTheme],
  );

  const handleDatesSet = (info: DatesSetArg) => {
    setCalendarDate(info.view.currentStart);
    setViewMode(info.view.type as CalendarViewMode);
    setVisibleRange({ start: info.start, end: info.end });

    if (!selectedVenue) {
      return;
    }

    setVisibleFromLocal(
      formatDateTimeForPicker(info.start, selectedVenue.timezone),
    );
    setVisibleToLocal(
      formatDateTimeForPicker(info.end, selectedVenue.timezone),
    );
  };

  const handleOpenCreate = () => {
    setSelectedBookingId(null);
    setEditingBookingId(null);
    setDraft(createEmptyBookingDraft(selectedVenue, preferredUnitId));
  };

  const handleSelect = (selection: DateSelectArg) => {
    if (readOnly) return;
    setSelectedBookingId(null);
    setEditingBookingId(null);
    setDraft(
      getSelectionDraft(
        selection,
        selectedVenue,
        preferredUnitId,
      ),
    );
  };

  const handleEventClick = (info: EventClickArg) => {
    const booking = getEventBooking(info.event);
    if (!booking) {
      return;
    }

    setDraft(null);
    setEditingBookingId(null);
    setSelectedBookingId(booking.id);
  };

  const handleEditBooking = () => {
    if (!selectedBooking) {
      return;
    }

    setEditingBookingId(selectedBooking.id);
    setDraft(createDraftFromBooking(selectedBooking));
  };

  const revealSavedBooking = (booking: ProviderBooking) => {
    // Saving can move a booking outside the currently loaded calendar window.
    // Keep its selection until the target window loads, just as for an inbox tap.
    if (booking.venue_id !== currentVenueIdRef.current) return;
    pendingRevealBookingIdRef.current = booking.id;
    const scheduledStart = new Date(booking.scheduled_start_at);
    setCalendarDate(scheduledStart);
    calendarRef.current?.getApi().gotoDate(scheduledStart);
    setPinnedBooking(booking);
    setSelectedBookingId(booking.id);
    setEditingBookingId(null);
    setDraft(null);
  };

  const handleSaveDraft = async (value: ProviderBookingDraft) => {
    if (!selectedVenue) {
      return;
    }

    if (editingBookingId) {
      const updated = await updateBooking(editingBookingId, {
        venue_id: selectedVenue.id,
        unit_id: value.unit_id,
        customer_name: value.customer_name,
        customer_email: value.customer_email || null,
        customer_phone: value.customer_phone || null,
        scheduled_start_local: value.scheduled_start_local,
        scheduled_end_local: value.scheduled_end_local,
        notes: value.notes || null,
      });

      if (updated) revealSavedBooking(updated);

      return;
    }

    const created = await createBooking({
      venue_id: selectedVenue.id,
      unit_id: value.unit_id,
      customer_name: value.customer_name,
      customer_email: value.customer_email || null,
      customer_phone: value.customer_phone || null,
      scheduled_start_local: value.scheduled_start_local,
      scheduled_end_local: value.scheduled_end_local,
      notes: value.notes || null,
    });

    if (created) revealSavedBooking(created);
  };

  const handleStatusChange = async (nextStatus: BookingStatus, note: string) => {
    if (!selectedBooking) {
      return;
    }

    const updated = await updateBookingStatus(selectedBooking.id, nextStatus, note);
    if (updated) {
      if (pinnedBooking?.id === updated.id) setPinnedBooking(updated);
      setSelectedBookingId(updated.id);
    }
  };

  const handleQuickStatus = async (booking: ProviderBooking, nextStatus: BookingStatus) => {
    const updated = await updateBookingStatus(booking.id, nextStatus, "");
    if (updated && pinnedBooking?.id === updated.id) setPinnedBooking(updated);
    return updated;
  };

  const handleStartSession = async (note: string) => {
    if (!selectedBooking) {
      return;
    }

    const session = await onStartSessionFromBooking(selectedBooking, note);
    if (!session) {
      return;
    }

    await reload();
    setSelectedBookingId(selectedBooking.id);
  };

  const handleReschedule = async (info: EventDropArg | BookingRescheduleInfo) => {
    if (readOnly) {
      info.revert();
      return;
    }
    const booking = getEventBooking(info.event);
    if (!booking) {
      info.revert();
      return;
    }

    if (!selectedVenue) {
      info.revert();
      return;
    }

    const updated = await updateBooking(booking.id, {
      venue_id: booking.venue_id,
      unit_id: booking.unit_id,
      customer_name: booking.customer_name,
      customer_email: booking.customer_email,
      customer_phone: booking.customer_phone,
      scheduled_start_local: formatDateTimeForPicker(
        info.event.start ?? new Date(booking.scheduled_start_at),
        selectedVenue.timezone,
      ),
      scheduled_end_local: formatDateTimeForPicker(
        info.event.end ?? new Date(booking.scheduled_end_at),
        selectedVenue.timezone,
      ),
      notes: booking.notes,
    });

    if (!updated) {
      info.revert();
      return;
    }

    setSelectedBookingId(updated.id);
  };

  const renderEventContent = (eventInfo: EventContentArg) => {
    const booking = getEventBooking(eventInfo.event);
    const eventTitle = booking
      ? `${booking.customer_name} · ${booking.unit_name}`
      : eventInfo.event.title || t("New booking");

    if (!booking) {
      return (
        <div
          aria-label={`${eventInfo.timeText} · ${eventTitle}`}
          className="provider-calendar-event grid min-w-0 gap-0.5 text-[11px] leading-tight"
          title={`${eventInfo.timeText} · ${eventTitle}`}
        >
          <span className="provider-calendar-event__time font-semibold">{eventInfo.timeText}</span>
          <span className="provider-calendar-event__line">{eventTitle}</span>
        </div>
      );
    }

    return (
      <div
        aria-label={`${eventInfo.timeText} · ${eventTitle}`}
        className="provider-calendar-event grid min-w-0 gap-0.5 text-[11px] leading-tight"
        title={`${eventInfo.timeText} · ${eventTitle}`}
      >
        <span className="provider-calendar-event__time font-semibold">{eventInfo.timeText}</span>
        <span className="provider-calendar-event__line">{booking.customer_name}</span>
        <span className="provider-calendar-event__line text-[10px] font-medium text-slate-700 dark:text-slate-200/80">
          {booking.unit_name}
        </span>
      </div>
    );
  };

  const calendarLabel = selectedVenue && isCalendarView
    ? visibleRange
      ? formatVisibleRangeLabel(visibleRange.start, visibleRange.end, selectedVenue.timezone, resolvedLanguage === "ar" ? "ar-EG" : "en-US", t("to"))
      : formatCalendarRangeLabel(viewMode, calendarDate, selectedVenue.timezone)
    : "";
  const pendingCount = resolvedBookings.filter((booking) => booking.status === "pending").length;

  const viewTabs: Array<[CalendarViewMode, string]> = [
    ["upcoming", t("Upcoming")],
    ["timeGridWeek", t("Week")],
    ["timeGridDay", t("Day")],
    ["past", t("Past")],
  ];

  return (
    <div className="ops-surface">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="ops-page-title">{t("Bookings")}</h1>
          <p className="ops-copy text-sm">
            {viewMode === "upcoming"
              ? pendingCount > 0
                ? t(pendingCount === 1 ? "{count} request waiting for you." : "{count} requests waiting for you.", { count: pendingCount })
                : t("The next 30 days.")
              : viewMode === "past"
                ? t("The last 30 days.")
                : t(resolvedBookings.length === 1 ? "{count} booking shown." : "{count} bookings shown.", { count: resolvedBookings.length })}
          </p>
        </div>
        {!readOnly ? (
          <button
            className="provider-schedule-new ops-button"
            data-variant="primary"
            onClick={handleOpenCreate}
            type="button"
          >
            {t("New booking")}
          </button>
        ) : null}
      </div>

      {notice && (
        <div className="ops-banner mt-3" data-tone="success">
          {notice}
        </div>
      )}

      {error && (
        <div className="ops-banner mt-3" data-tone="danger">
          {error}
        </div>
      )}

      <div className="mt-3 grid gap-3">
        <div className="grid gap-3">
          <div className="provider-schedule-toolbar flex flex-wrap items-center gap-2">
            <div aria-label={t("Show")} className="pn-segment" role="group">
              {viewTabs.map(([nextView, label]) => (
                <button
                  aria-pressed={viewMode === nextView}
                  key={nextView}
                  onClick={() => {
                    if (nextView === "timeGridDay" || nextView === "timeGridWeek") {
                      calendarRef.current?.getApi().changeView(nextView);
                    }
                    setViewMode(nextView);
                  }}
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>

            {isCalendarView ? (
              <>
                <div className="inline-flex items-center gap-1">
                  {[
                    ["prev", t("Previous")],
                    ["today", t("Today")],
                    ["next", t("Next")],
                  ].map(([action, label]) => (
                    <button
                      className="ops-button"
                      data-size="sm"
                      data-variant="secondary"
                      key={action}
                      onClick={() => {
                        const api = calendarRef.current?.getApi();
                        if (!api) return;
                        if (action === "prev") api.prev();
                        else if (action === "next") api.next();
                        else api.today();
                      }}
                      type="button"
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="provider-schedule-range text-sm font-semibold">{calendarLabel}</p>
              </>
            ) : null}
          </div>

          {!selectedVenue ? (
            <div className="ops-empty px-4 py-10 text-center">
              {t("Select a venue first. The venue picker above acts as the primary booking filter.")}
            </div>
          ) : selectedVenue.units.length === 0 ? (
            <div className="ops-empty px-4 py-10 text-center">
              {t("Add at least one unit before scheduling bookings for {name}.", { name: selectedVenue.name })}
            </div>
          ) : viewMode === "upcoming" ? (
            <UpcomingBookings
              bookings={resolvedBookings}
              loading={isLoading}
              onOpen={(bookingId) => {
                setDraft(null);
                setEditingBookingId(null);
                setSelectedBookingId(bookingId);
              }}
              onStatus={handleQuickStatus}
              readOnly={readOnly}
              timezone={selectedVenue.timezone}
              updatingStatusId={updatingStatusId}
            />
          ) : viewMode === "past" ? (
            <BookingListView
              bookings={resolvedBookings}
              loading={isLoading}
              onSelectBooking={(bookingId) => {
                setDraft(null);
                setEditingBookingId(null);
                setSelectedBookingId(bookingId);
              }}
              selectedBookingId={selectedBookingId}
            />
          ) : (
            <div className="provider-calendar-shell">
              <div className="provider-calendar-scroll">
                <FullCalendar
                  ref={calendarRef}
                  allDaySlot={false}
                  datesSet={handleDatesSet}
                  dayHeaderFormat={dayHeaderFormat}
                  editable={!readOnly}
                  eventClick={handleEventClick}
                  eventContent={renderEventContent}
                  eventDrop={readOnly ? undefined : (info) => void handleReschedule(info)}
                  eventResize={readOnly ? undefined : (info) => void handleReschedule(info)}
                  events={calendarEvents}
                  expandRows
                  firstDay={getVenueTodayDayOfWeek(selectedVenue.timezone)}
                  headerToolbar={false}
                  height="auto"
                  initialDate={calendarDate}
                  initialView={viewMode}
                  nowIndicator
                  plugins={[timeGridPlugin, interactionPlugin, luxonPlugin]}
                  select={readOnly ? undefined : (info) => handleSelect(info)}
                  selectable={!readOnly}
                  selectMirror={!readOnly}
                  slotDuration="00:30:00"
                  slotLabelFormat={slotLabelFormat}
                  slotMaxTime={formatSlotTime(slotMaxMinutes)}
                  slotMinTime={formatSlotTime(slotMinMinutes)}
                  timeZone={selectedVenue.timezone}
                />
              </div>
            </div>
          )}

          {/* Refreshes keep the calendar in place: a fixed-height line that only changes its text. */}
          <p aria-live="polite" className="provider-refresh-line">
            {isLoading && resolvedBookings.length > 0 ? t("Updating bookings…") : ""}
          </p>
        </div>

      </div>

      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            setDraft(null);
            setEditingBookingId(null);
          }
        }}
        open={Boolean(draft)}
      >
        <DialogContent aria-describedby={undefined} className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogTitle className="sr-only">
            {editingBookingId ? t("Edit booking") : t("Create booking")}
          </DialogTitle>
          {draft ? (
            <BookingFormCard
              booking={editingBookingId ? resolvedBookings.find((booking) => booking.id === editingBookingId) ?? selectedBooking ?? undefined : null}
              initialValue={draft}
              isSubmitting={creating || Boolean(editingBookingId && savingBookingId === editingBookingId)}
              onCancel={() => {
                setDraft(null);
                setEditingBookingId(null);
              }}
              onSubmit={handleSaveDraft}
              preferredUnitId={preferredUnitId}
              selectedVenue={selectedVenue}
              submitLabel={editingBookingId ? t("Save booking") : t("Create booking")}
              title={editingBookingId ? t("Edit booking") : t("Create booking")}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            setSelectedBookingId(null);
          }
        }}
        open={Boolean(selectedBooking) && !draft}
      >
        <DialogContent aria-describedby={undefined} className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogTitle className="sr-only">{t("Booking detail")}</DialogTitle>
          {selectedBooking ? (
            <BookingDetailCard
              activeSession={selectedBookingSession}
              booking={selectedBooking}
              isStartingSession={startingSessionBookingId === selectedBooking.id}
              isUpdatingStatus={updatingStatusId === selectedBooking.id}
              onEdit={handleEditBooking}
              onStartSession={handleStartSession}
              onStatusChange={handleStatusChange}
              readOnly={readOnly}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
